// 우측 컨텐츠: 글 목록 / 상세 / 에디터 전환
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Category, Post } from '@shared/types';
import { api } from '../core/ApiClient';
import { push } from '../core/PushManager';
import { Editor } from './Editor';
import { ConfirmDialog } from './ConfirmDialog';
import { Toast } from './Toast';
import { ReadButton } from './ReadButton';
import { markdownToHtml } from '../core/markdown';

type Mode = 'list' | 'detail' | 'editor';

interface Props {
  categories: Category[];
  categoryId: number | null;
  onTitleChange: (title: string) => void;
  /** 카테고리 클릭 시퀀스. 같은 카테고리를 다시 눌러도 목록으로 복귀시키기 위한 트리거. */
  navSeq?: number;
  /** 딥링크(?post=)로 지정된 글 id. 최초 진입 시 상세로 바로 연다. */
  initialPostId?: number | null;
  /** 딥링크 처리 완료 통지 (부모가 상태를 소거하도록) */
  onInitialConsumed?: () => void;
}

export function ContentView({
  categories,
  categoryId,
  onTitleChange,
  navSeq,
  initialPostId,
  onInitialConsumed,
}: Props) {
  const [mode, setMode] = useState<Mode>('list');
  const [posts, setPosts] = useState<Post[]>([]);
  const [selected, setSelected] = useState<Post | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Post | null>(null);
  const [toast, setToast] = useState('');
  const consumedInitialRef = useRef(false);
  const detailPushedRef = useRef(false); // 상세 진입 시 쌓은 히스토리 항목 보유 여부

  const loadPosts = async (catId: number) => {
    const list = await api.listPosts(catId);
    setPosts(list);
  };

  useEffect(() => {
    onTitleChange('');
    // 딥링크(?post=): 최초 1회, 카테고리와 무관하게 지정 글 상세로 진입
    if (initialPostId && !consumedInitialRef.current) {
      consumedInitialRef.current = true;
      void (async () => {
        const post = await api.getPost(initialPostId);
        openDetail(post);
        onInitialConsumed?.();
      })();
      return;
    }
    setMode('list');
    setSelected(null);
    if (categoryId) void loadPosts(categoryId);
    else setPosts([]);
  }, [categoryId, navSeq, initialPostId]);

  // 목록으로 전환(히스토리 조작 없음). 뒤로가기/‘목록’ 버튼이 공통으로 사용.
  const goToList = () => {
    setSelected(null);
    setMode('list');
    onTitleChange('');
    if (categoryId) void loadPosts(categoryId);
  };

  // 목록 카드 클릭 → 상세. 목록 응답은 성능상 content를 비워 보내므로 전체 글을 조회해 연다.
  const openPost = async (id: number) => {
    const post = await api.getPost(id);
    openDetail(post);
  };

  // 상세 열기: 최초 진입 시 히스토리 항목을 1개 쌓아 브라우저 뒤로가기로 목록에 복귀 가능하게 함
  const openDetail = (post: Post, pushHistory = true) => {
    setSelected(post);
    setMode('detail');
    onTitleChange(post.title);
    if (pushHistory && !detailPushedRef.current) {
      history.pushState({ blogDetail: post.id }, '');
      detailPushedRef.current = true;
    }
  };

  // ‘목록’ 버튼/삭제 후: 쌓아둔 항목이 있으면 뒤로가기로 소비(→ popstate 가 목록 전환 처리)
  const backToList = () => {
    if (detailPushedRef.current) history.back();
    else goToList();
  };

  const onSaved = (post: Post) => {
    openDetail(post);
  };

  // 마우스/키보드 뒤로가기(popstate): 우리가 쌓은 상세 항목이 빠지면 목록으로 복귀
  useEffect(() => {
    const onPop = () => {
      if (!detailPushedRef.current) return;
      detailPushedRef.current = false;
      goToList();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [categoryId]);

  const doDelete = async () => {
    const post = confirmDelete;
    setConfirmDelete(null);
    if (!post) return;
    // 이 기기의 알림 아이콘이 활성 상태일 때만 삭제 알림 브로드캐스트
    await api.deletePost(post.id, push.active);
    backToList();
  };

  const doShare = async (post: Post) => {
    const url = `${location.origin}/?post=${post.id}`;
    // 모바일 등 네이티브 공유 지원 시 공유 시트, 아니면 링크 복사
    if (navigator.share) {
      await navigator.share({ title: post.title, url }).catch(() => undefined);
      return;
    }
    await navigator.clipboard.writeText(url).then(
      () => setToast('링크가 복사되었습니다.'),
      () => setToast('링크 복사에 실패했습니다.'),
    );
  };

  if (categoryId === null) {
    return <div class="empty">먼저 카테고리를 선택하거나 설정에서 추가하세요.</div>;
  }

  if (mode === 'editor') {
    return (
      <Editor
        categories={categories}
        initial={selected}
        defaultCategoryId={categoryId}
        onSaved={onSaved}
        onCancel={() => (selected ? openDetail(selected) : setMode('list'))}
      />
    );
  }

  if (mode === 'detail' && selected) {
    return (
      <>
      <article class="post-detail">
        <div class="post-detail__head">
          <h1 class="post-detail__title">{selected.title}</h1>
          <div class="row">
            <ReadButton
              kind="post"
              id={selected.id}
              version={selected.updatedAt}
              count={selected.readCount}
              onIncrement={() => api.markPostRead(selected.id)}
              onCounted={(readCount) => setSelected({ ...selected, readCount })}
            />
            <button class="btn" onClick={() => void doShare(selected)}>
              공유
            </button>
            <button class="btn" onClick={backToList}>
              목록
            </button>
            <button class="btn" onClick={() => setMode('editor')}>
              수정
            </button>
            <button class="btn btn--danger" onClick={() => setConfirmDelete(selected)}>
              삭제
            </button>
          </div>
        </div>
        <div class="post-detail__meta">
          등록: {selected.createdAt} · 수정: {selected.updatedAt}
        </div>
        <div
          class="post-detail__body prose"
          dangerouslySetInnerHTML={{
            __html: selected.format === 'md' ? markdownToHtml(selected.content) : selected.content,
          }}
        />
        {selected.attachments.length > 0 && (
          <div class="attach-list">
            {selected.attachments.map((a) => (
              <a key={a.id} class="attach-chip" href={a.url} target="_blank" rel="noreferrer">
                📎 {a.originalName}
              </a>
            ))}
          </div>
        )}
      </article>
      {confirmDelete && (
        <ConfirmDialog
          title="글 삭제"
          message="이 글과 첨부파일을 삭제합니다. 삭제하면 복구할 수 없습니다. 계속하시겠습니까?"
          confirmLabel="삭제"
          danger
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {toast && <Toast message={toast} onDone={() => setToast('')} />}
      </>
    );
  }

  // list
  return (
    <div>
      <div class="row" style="margin-bottom:16px">
        <h2 style="margin:0">
          {categories.find((c) => c.id === categoryId)?.name ?? '글 목록'}
        </h2>
        <div class="spacer" />
        <button class="btn btn--primary" onClick={() => (setSelected(null), setMode('editor'))}>
          + 새 글
        </button>
      </div>

      {posts.length === 0 ? (
        <div class="empty">글이 없습니다. 첫 글을 작성해 보세요.</div>
      ) : (
        <div class="post-list">
          {posts.map((p) => (
            <div key={p.id} class="post-card" onClick={() => void openPost(p.id)}>
              <h3 class="post-card__title">{p.title}</h3>
              <div class="post-card__meta">
                등록 {p.createdAt} · 👁 {p.readCount}
                {p.attachments.length > 0 && ` · 📎 ${p.attachments.length}`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
